import React, { useEffect, useState } from 'react';

/* Player tags this browser has looked up, kept per game in localStorage so a
   visitor can flip between their own account, a friend's and an alt without
   retyping. Nothing leaves the browser. */
const MAX_TAGS = 8;
const EVENT = 'statyoyo:recent-tags';

const storageKey = (game) => `statyoyo.recentTags.${game}`;

// Stored without the leading '#', uppercase, so '#2pp' and '2PP' are one entry.
export function normalizeTag(tag) {
  return String(tag || '').trim().replace(/^#/, '').toUpperCase();
}

function read(game) {
  try {
    const list = JSON.parse(window.localStorage.getItem(storageKey(game)) || '[]');
    return Array.isArray(list) ? list.filter((e) => e && e.tag) : [];
  } catch {
    return [];
  }
}

function write(game, list) {
  try {
    window.localStorage.setItem(storageKey(game), JSON.stringify(list));
  } catch {
    // Private mode or storage full: the switcher just stays empty.
  }
  window.dispatchEvent(new CustomEvent(EVENT, { detail: game }));
}

// Moves the tag to the front, keeping the last known name if none is given.
export function rememberTag(game, tag, name) {
  const t = normalizeTag(tag);
  if (!t) return;
  const list = read(game);
  const prev = list.find((e) => e.tag === t);
  const entry = { tag: t, name: name || prev?.name || null };
  write(game, [entry, ...list.filter((e) => e.tag !== t)].slice(0, MAX_TAGS));
}

export function forgetTag(game, tag) {
  const t = normalizeTag(tag);
  write(game, read(game).filter((e) => e.tag !== t));
}

export function useRecentTags(game) {
  const [list, setList] = useState(() => read(game));
  useEffect(() => {
    const sync = () => setList(read(game));
    sync();
    const onEvent = (e) => { if (e.detail === game) sync(); };
    const onStorage = (e) => { if (e.key === storageKey(game)) sync(); };
    window.addEventListener(EVENT, onEvent);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(EVENT, onEvent);
      window.removeEventListener('storage', onStorage);
    };
  }, [game]);
  return list;
}

/* One-tap switcher under a tag box. `onPick` gets the stored tag (no '#');
   `current` highlights the tag on screen now. */
export function RecentTags({ game, current, onPick, label = 'Your tags' }) {
  const list = useRecentTags(game);
  if (!list.length) return null;
  const cur = normalizeTag(current);
  return (
    <div className="recent-tags" aria-label={label}>
      <span className="recent-tags-label">{label}</span>
      {list.map((e) => (
        <span key={e.tag} className={`recent-tag${e.tag === cur ? ' on' : ''}`}>
          <button type="button" className="recent-tag-pick" onClick={() => onPick(e.tag)} title={`#${e.tag}`}>
            {e.name ? <><b>{e.name}</b> <span className="recent-tag-id">#{e.tag}</span></> : <b>#{e.tag}</b>}
          </button>
          <button
            type="button"
            className="recent-tag-x"
            onClick={() => forgetTag(game, e.tag)}
            aria-label={`Forget #${e.tag}`}
            title="Forget this tag"
          >
            ×
          </button>
        </span>
      ))}
    </div>
  );
}
